import dayjs from "dayjs";
import { setPath } from "../../utils/object";

// Builds sample rows for a resource (see pages/Resource/resources.js) so every
// list page has data in mock mode. Explicit `resource.seed` rows win; any
// column/field they leave out is still generated. Nested keys
// ("createdByUser.firstname") produce nested objects, like the real API rows.

const NAMES = [
  ["Karim", "Ahmed"],
  ["Sakib", "Khan"],
  ["Nusrat", "Jahan"],
  ["Rafiq", "Islam"],
  ["Tania", "Akter"],
  ["Mahmud", "Hasan"],
  ["Farhana", "Yeasmin"],
  ["Imran", "Hossain"],
];
const COMPANIES = ["Sky Travels", "Green Trade", "Blue Wave Ltd", "Alpha Traders", "Delta Corp"];
const DEVICES = ["Chrome on Windows", "Safari on iPhone", "Edge on Windows", "Firefox on Linux"];
const PARTICULARS = ["Ticket Sale", "Deposit", "Refund", "Service Charge", "Commission", "Adjustment"];
const BANKS = ["Islami Bank", "City Bank", "Dutch-Bangla Bank", "BRAC Bank", "bKash"];

const fullName = (i) => NAMES[i % NAMES.length];

const textValue = (column, i) => {
  const key = String(column.key);
  const last = key.split(".").pop();
  if (/ip(Address)?$|^userIp$/i.test(last)) return `103.${(i * 13) % 255}.12.${(i * 7) % 255}`;
  if (/device(Name)?$/i.test(last)) return DEVICES[i % DEVICES.length];
  if (/deviceOS/i.test(last)) return ["Windows 11", "iOS 17", "Windows 10", "Ubuntu 22"][i % 4];
  if (/browser/i.test(last)) return ["Chrome", "Safari", "Edge", "Firefox"][i % 4];
  if (/email/i.test(last)) return `user${i + 1}@deshmart.com`;
  if (/phoneNo|PhoneNo|phone|msisdn/i.test(last)) return String(1700000000 + i * 37151).slice(0, 10);
  if (/phoneCode/i.test(last)) return "+880";
  if (/particulars|narration/i.test(last)) return PARTICULARS[i % PARTICULARS.length];
  if (/bankName/i.test(last)) return BANKS[i % BANKS.length];
  if (/bankAccountName|accountName/i.test(last)) return `${COMPANIES[i % COMPANIES.length]} A/c`;
  if (/accountNumber|AccountNumber/.test(last)) return String(1001200000 + i * 4321);
  if (/branch/i.test(last)) return ["Gulshan", "Motijheel", "Dhanmondi", "Uttara"][i % 4];
  if (/companyName|agencyName/i.test(last)) return COMPANIES[i % COMPANIES.length];
  if (/(vendorName)/i.test(last)) return `Vendor ${i + 1}`;
  if (/address/i.test(last)) return `House ${10 + i}, Road ${(i % 9) + 1}, Dhaka`;
  if (/(No|Id|Ref|Reference|Code)$/.test(last) || /reference/i.test(last))
    return `${String(column.label || last).replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase()}-${1000 + i}`;
  if (/(name|user|party|receivedFrom|receivedBy|assignedTo|changedBy)$/i.test(last)) {
    const [first, lastName] = fullName(i);
    return `${first} ${lastName}`;
  }
  return `${column.label || key} ${i + 1}`;
};

const valueFor = (column, i) => {
  if (typeof column.mock === "function") return column.mock(i);
  if (column.options?.length) {
    const option = column.options[i % column.options.length];
    return typeof option === "object" ? option.value : option;
  }

  // Related records the API returns as objects / arrays (rendered by column.value)
  const key = String(column.key);
  if (/b2bUser$/.test(key)) return { identificationShortCode: `AG-${1000 + i}`, companyName: COMPANIES[i % COMPANIES.length] };
  if (/^vendor$/.test(key)) return { identificationShortCode: `V-${100 + i}`, vendorName: `Vendor ${i + 1}` };
  if (/loanParty$/.test(key)) return { identificationShortCode: `LP-${100 + i}`, personName: fullName(i).join(" ") };
  if (/companyAccounts$/.test(key)) return [{ bankName: BANKS[i % BANKS.length] }, { bankName: BANKS[(i + 1) % BANKS.length] }].slice(0, 1 + (i % 2));
  if (/(assignedToUser|updatedByUser|approvedByw*User)$/.test(key)) {
    const [first, last] = fullName(i);
    return { identificationShortCode: `EMP-${100 + i}`, firstname: first, lastname: last };
  }

  switch (column.type) {
    case "number":
      return (i + 1) * 10;
    case "amount":
    case "money":
      return (i + 1) * 2500 + 500 * (i % 3);
    case "date":
      return dayjs().subtract(i, "day").format("YYYY-MM-DD");
    case "datetime":
    case "datetime-local":
      return dayjs().subtract(i * 3, "hour").toISOString();
    case "status":
    case "yesno":
      return i % 4 !== 3;
    case "user": {
      const [first, last] = fullName(i);
      return { identificationShortCode: `EMP-${100 + i}`, firstname: first, lastname: last };
    }
    case "name": {
      const [first, last] = fullName(i);
      return { firstname: first, lastname: last };
    }
    case "join":
      return [{ name: "View" }, { name: "Create" }, { name: "Edit" }].slice(0, 1 + (i % 3));
    case "image":
      return "";
    case "select":
    case "relation":
      return (i % 5) + 1;
    case "multirelation":
      return [(i % 4) + 1];
    case "policies":
      return [1, 2, 3];
    case "phonecode":
      return "+880";
    case "richtext":
      return `<p>${column.label || "Message"} for sample ${i + 1}</p>`;
    case "textarea":
      return `${column.label || ""} for sample ${i + 1}`.trim();
    case "email":
      return `user${i + 1}@deshmart.com`;
    case "password":
      return undefined;
    case "file":
      return undefined;
    default:
      return textValue(column, i);
  }
};

export const buildSeed = (resource, count = 25) => {
  // A list column with no options of its own borrows its form field's (e.g. Banner "User Type")
  const fieldByName = Object.fromEntries(resource.fields.map((field) => [field.name, field]));
  const columns = resource.columns
    .filter((c) => c.key)
    .map((c) => (c.options || !fieldByName[c.key]?.options ? c : { ...c, options: fieldByName[c.key].options }));
  const fieldColumns = resource.fields
    .filter((field) => field.name && !columns.some((c) => c.key === field.name))
    .map((field) => ({
      key: field.name,
      label: field.label,
      type: field.type,
      options: field.options,
      mock: field.mock,
    }));
  const all = [...columns, ...fieldColumns];
  const size = resource.seed ? resource.seed.length : count;
  let balance = 0;

  return Array.from({ length: size }, (_, i) => {
    const row = { id: i + 1 };
    all.forEach((column) => {
      const value = valueFor(column, i);
      if (value !== undefined) setPath(row, column.key, value);
    });

    if (!row.createdDateTime) row.createdDateTime = dayjs().subtract(i * 5, "hour").toISOString();
    if (!row.createdByUser) {
      const [first, last] = fullName(i);
      row.createdByUser = { identificationShortCode: `EMP-${100 + i}`, firstname: first, lastname: last };
    }
    if (row.isActive === undefined && resource.fields.some((f) => f.name === "isActive")) {
      row.isActive = true;
    }

    // Ledger rows: either a debit or a credit, with a running balance
    if ("debit" in row || "credit" in row) {
      const amount = (i + 1) * 2500;
      row.debit = i % 2 ? 0 : amount;
      row.credit = i % 2 ? amount : 0;
      balance += row.credit - row.debit;
      ["balance", "currentBalance", "cashBalance"].forEach((key) => {
        if (key in row) row[key] = balance;
      });
    }
    // Currency change log: previous -> updated
    if ("previousData" in row) {
      const code = ["USD", "EUR", "GBP", "INR", "SAR"][i % 5];
      row.currency = { currencyCode: code };
      row.previousData = { currencyToBDT: 120 + i, bdtToCurrency: 0.0083, isActive: true };
      row.updatedData = { currencyToBDT: 121 + i, bdtToCurrency: 0.0083, isActive: true };
    }
    // Loan: principal + interest = total, installments
    if ("principleAmount" in row) {
      row.principleAmount = 100000 + i * 10000;
      row.interestRateInPercentage = 10;
      row.interestAmount = row.principleAmount * 0.1;
    }
    // Fixed values the resource always sends/filters on (type: "Income" ...)
    Object.assign(row, resource.listParams, resource.submitExtras);

    return { ...row, ...(resource.seed?.[i] || {}) };
  });
};
